// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { RootState } from 'Store';
import { API_URL } from 'Config';
import { getOrders } from 'Features/order/orderSelectors';
import { AW_SERVER_CHAT_BASE_URL } from './constants';

/**
 * Resolve the order to act on: the viewer's selected order → a placed order that owns a gdb →
 * the first placed. Shared by the chat endpoint, the session key, and the share API so they all
 * point at the same order.
 */
export const resolveChatOrder = (state: RootState) => {
  const orders = getOrders(state);
  const placed = orders.placedOrders ?? [];
  const selectedOrderId = state.map.editor.present.orderSelected;
  return placed.find((candidate) => candidate._id === selectedOrderId)
    ?? placed.find((candidate) => (candidate.geoDbFiles?.length ?? 0) > 0)
    ?? placed[0];
};

export interface OrderContext {
  base: string;
  orgId: string;
  projectId: string;
  orderId: string;
  token?: string;
}

/**
 * The aw-server order context (base url + org/project/order ids + auth token) for the current order,
 * or undefined when it can't be built (no org/project/order). Used to call the order-scoped
 * aw-server routes (gdb-chat, session, shared pins).
 */
export const buildOrderContext = (state: RootState): OrderContext | undefined => {
  const order = resolveChatOrder(state);
  const orgId = state.account.org?._id;
  const projectFromUrl = window.location.pathname.match(/\/projects\/([a-f\d]{24})/i)?.[1];
  const projectId = order?.ownerProject ?? state.project.project?._id ?? projectFromUrl;
  const orderId = order?._id ?? projectId;
  const token = state.auth?.token;
  if (!orgId || !projectId || !orderId) return undefined;
  const base = AW_SERVER_CHAT_BASE_URL || API_URL;
  return {
    base, orgId, projectId, orderId, token,
  };
};
